import useAsyncAction from 'hooks/useAsyncAction'
import { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { from } from 'rxjs'
import { selectComposerFBAlbum } from 'services/compose'
import { composerPostSelector } from 'services/compose/selectors'
import { getAlbums } from 'services/destinations'
import { BRAND_FACEBOOK } from 'shared/constants'
import { ComposerFacebookPost, FBAlbum, PostDestination } from 'shared/types'
import StoreState, { StoreThunkDispatch } from 'store/state'
import { AlbumSelector } from './AlbumSelector'

interface FacebookAlbumSelectorOwnProps {
  profile: PostDestination
  hideProfileImage?: boolean
  raised?: boolean
  className?: string
  selectedAlbumsCustom?: FBAlbum[]
  onSelectedAlbumChangeCustom?: (profile: PostDestination, album: FBAlbum | undefined) => void
}

interface FacebookAlbumSelectorConnectedProps {
  selectedAlbums: FBAlbum[]
  fetchAlbums: (profile: PostDestination) => Promise<any>
  onSelectedAlbumChange: (profile: PostDestination, album: FBAlbum | undefined) => void
}

type FacebookAlbumSelectorProps = FacebookAlbumSelectorOwnProps & FacebookAlbumSelectorConnectedProps

export function FacebookAlbumSelector(props: FacebookAlbumSelectorProps) {
  const [albums, setAlbums] = useState(props.profile.fbAlbums || {})
  const [triggerFetchAlbums, response, error, loading] = useAsyncAction((p) => from(props.fetchAlbums(p)))
  const didFetch$ = useRef(false)

  useEffect(() => {
    if (Object.keys(albums).length < 1 && !error && !didFetch$.current) {
      triggerFetchAlbums(props.profile)
      didFetch$.current = true
    }
  }, [props.profile.id, triggerFetchAlbums, props.profile, error, albums])

  useEffect(() => {
    if (response) {
      setAlbums(response.albums)
    }
  }, [response])

  const onSelectedAlbumChange = (a: FBAlbum) => {
    if (props.onSelectedAlbumChangeCustom) {
      props.onSelectedAlbumChangeCustom(props.profile, a)
    } else {
      props.onSelectedAlbumChange(props.profile, a)
    }
  }

  const selectedAlbums = props.selectedAlbumsCustom || props.selectedAlbums
  const selected = selectedAlbums.find(a => a.ppid === props.profile.ppid)

  return (
    <AlbumSelector
      id="facebook-album-selector"
      profile={props.profile}
      albums={albums}
      selectedAlbumId={selected?.id}
      allowEmpty
      loading={loading}
      messageEmptySelection={<FormattedMessage id="composer.labels.fb-album-default" />}
      errorMessageElement={<FormattedMessage id="composer.labels.albums-fetch-failed" />}
      className={props.className}
      raised={props.raised}
      hideProfileImage={props.hideProfileImage}
      onSelectionChange={onSelectedAlbumChange}
    />
  )
}

function mapStateToProps(state: StoreState) {
  const fbPost = composerPostSelector(state, BRAND_FACEBOOK) as ComposerFacebookPost

  return {
    selectedAlbums: fbPost.albums
  }
}

function mapDispatchToProps(dispatch: StoreThunkDispatch) {
  return {
    fetchAlbums: (profile: PostDestination) => dispatch(getAlbums(profile)).unwrap(),
    onSelectedAlbumChange: (profile: PostDestination, album: FBAlbum | undefined) => dispatch(selectComposerFBAlbum({ profile, album }))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(FacebookAlbumSelector)
